import React, { useEffect, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableHeader from "@tiptap/extension-table-header";
import TableCell from "@tiptap/extension-table-cell";
import {
  Bold,
  Code,
  Columns3,
  Heading1,
  Heading2,
  Heading3,
  ImagePlus,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Quote,
  Redo2,
  Rows3,
  Strikethrough,
  Table as TableIcon,
  Trash2,
  Undo2,
} from "lucide-react";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { v4 } from "uuid";
import toast from "react-hot-toast";
import { storage } from "../../Firebase/Firebase.utils";
import { storagePath } from "../utils/cmsSchema";

function ToolbarButton({ onClick, active, disabled, title, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`w-8 h-8 inline-flex items-center justify-center rounded-md text-sm transition-colors ${
        active
          ? "bg-leechiuBlue text-white"
          : "text-gray-600 hover:bg-gray-100"
      } disabled:opacity-40 disabled:cursor-not-allowed`}
    >
      {children}
    </button>
  );
}

export default function RichTextEditor({
  value,
  onChange,
  collection,
  docId,
  placeholder = "Write your content…",
  onUploadingChange,
}) {
  const fileInputRef = useRef(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  // Track whether the next `value` change came from the editor itself
  // (via `onUpdate`). When it did, skip the imperative `setContent` in
  // the syncing effect — otherwise we wipe the just-inserted image.
  const isInternalUpdateRef = useRef(false);
  // Remember where the cursor was when the user clicked "Insert image",
  // so we can re-insert at the same spot once the upload completes
  // (the file picker drops the editor's selection).
  const insertPosRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: { keepMarks: true },
        orderedList: { keepMarks: true },
      }),
      Link.configure({ openOnClick: false, autolink: true }),
      Image.configure({ inline: false }),
      Placeholder.configure({ placeholder }),
      // Tables: the four extensions are inseparable — Table is the
      // container, TableRow / TableHeader / TableCell are the cells.
      // `resizable: true` adds column-resize drag handles.
      Table.configure({ resizable: true, HTMLAttributes: { class: "cms-table" } }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class: "cms-prose max-w-none focus:outline-none min-h-[280px] px-5 py-4",
      },
    },
    onUpdate: ({ editor }) => {
      isInternalUpdateRef.current = true;
      onChange?.(editor.getHTML());
    },
  });

  useEffect(() => {
    if (!editor) return;
    if (isInternalUpdateRef.current) {
      isInternalUpdateRef.current = false;
      return;
    }
    const current = editor.getHTML();
    if ((value || "") !== current) {
      editor.commands.setContent(value || "", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return null;

  const setUploading = (b) => {
    setIsUploadingImage(b);
    onUploadingChange?.(b);
  };

  const insertLink = () => {
    const previous = editor.getAttributes("link").href;
    const url = window.prompt("Enter URL", previous || "https://");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const onClickInsertImage = () => {
    // Capture the caret position now, while the editor still has it.
    // Once the OS file picker opens, focus is dropped.
    insertPosRef.current = editor?.state?.selection?.from ?? null;
    fileInputRef.current?.click();
  };

  const onPickImage = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !editor) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image too large (max 10 MB).");
      return;
    }
    const path = storagePath({
      collection,
      docId,
      kind: "inline",
      filename: `${v4()}-${file.name}`,
    });
    const storageRef = ref(storage, path);
    const task = uploadBytesResumable(storageRef, file);
    setUploading(true);

    task.on(
      "state_changed",
      undefined,
      (err) => {
        console.error("[CMS] Image upload failed:", err);
        toast.error("Image upload failed.");
        setUploading(false);
        insertPosRef.current = null;
      },
      async () => {
        try {
          const url = await getDownloadURL(task.snapshot.ref);
          const target = insertPosRef.current;
          const chain = editor.chain();
          const focused =
            target != null
              ? chain.focus(target, { scrollIntoView: true })
              : chain.focus("end");
          const ok = focused.setImage({ src: url }).run();
          if (!ok) {
            // Fallback: append at end if the captured position is no
            // longer valid (e.g., user heavily edited during upload).
            editor.chain().focus("end").setImage({ src: url }).run();
          }
          toast.success("Image inserted.");
        } catch (err) {
          console.error("[CMS] Image insert failed:", err);
          toast.error("Failed to insert image.");
        } finally {
          setUploading(false);
          insertPosRef.current = null;
        }
      }
    );
  };

  return (
    <div className="border border-gray-200 rounded-xl bg-white overflow-hidden">
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-gray-100 bg-gray-50">
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive("bold")}
          title="Bold"
        >
          <Bold className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive("italic")}
          title="Italic"
        >
          <Italic className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleStrike().run()}
          active={editor.isActive("strike")}
          title="Strikethrough"
        >
          <Strikethrough className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleCode().run()}
          active={editor.isActive("code")}
          title="Inline code"
        >
          <Code className="w-4 h-4" />
        </ToolbarButton>

        <span className="w-px h-5 bg-gray-200 mx-1" />

        <ToolbarButton
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 1 }).run()
          }
          active={editor.isActive("heading", { level: 1 })}
          title="Heading 1"
        >
          <Heading1 className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 2 }).run()
          }
          active={editor.isActive("heading", { level: 2 })}
          title="Heading 2"
        >
          <Heading2 className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 3 }).run()
          }
          active={editor.isActive("heading", { level: 3 })}
          title="Heading 3"
        >
          <Heading3 className="w-4 h-4" />
        </ToolbarButton>

        <span className="w-px h-5 bg-gray-200 mx-1" />

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive("bulletList")}
          title="Bulleted list"
        >
          <List className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive("orderedList")}
          title="Numbered list"
        >
          <ListOrdered className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          active={editor.isActive("blockquote")}
          title="Blockquote"
        >
          <Quote className="w-4 h-4" />
        </ToolbarButton>

        <span className="w-px h-5 bg-gray-200 mx-1" />

        <ToolbarButton
          onClick={insertLink}
          active={editor.isActive("link")}
          title="Insert link"
        >
          <LinkIcon className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={onClickInsertImage}
          title="Insert image"
          disabled={isUploadingImage}
        >
          <ImagePlus className="w-4 h-4" />
        </ToolbarButton>

        <span className="w-px h-5 bg-gray-200 mx-1" />

        {/* Table controls. Insert is always enabled; the row/column/
            delete buttons only light up when the cursor is inside a
            table. Tiptap's `can()` chain reflects the current state. */}
        <TableSizePicker
          onPick={(rows, cols) =>
            editor
              .chain()
              .focus()
              .insertTable({ rows, cols, withHeaderRow: true })
              .run()
          }
        />
        <ToolbarButton
          onClick={() => editor.chain().focus().addRowAfter().run()}
          disabled={!editor.can().addRowAfter()}
          title="Add row below"
        >
          <Rows3 className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().addColumnAfter().run()}
          disabled={!editor.can().addColumnAfter()}
          title="Add column to the right"
        >
          <Columns3 className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().deleteTable().run()}
          disabled={!editor.can().deleteTable()}
          title="Delete table"
        >
          <Trash2 className="w-4 h-4" />
        </ToolbarButton>

        <span className="ml-auto" />

        <ToolbarButton
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          title="Undo"
        >
          <Undo2 className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          title="Redo"
        >
          <Redo2 className="w-4 h-4" />
        </ToolbarButton>
      </div>

      {isUploadingImage ? (
        <div className="px-3 py-1.5 text-xs text-leechiuBlue bg-blue-50 border-b border-blue-100">
          Uploading image…
        </div>
      ) : null}

      <EditorContent editor={editor} />

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onPickImage}
      />
    </div>
  );
}

/**
 * Google-Docs-style table size picker. Click the button to open a 10×8
 * hover grid; the highlighted area follows the cursor, the label shows
 * the resolved size, and click commits. Escape and outside clicks close
 * the popover without inserting.
 */
const PICKER_MAX_ROWS = 8;
const PICKER_MAX_COLS = 10;

function TableSizePicker({ onPick }) {
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState({ rows: 1, cols: 1 });
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClickAway = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClickAway);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClickAway);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const commit = (rows, cols) => {
    onPick(rows, cols);
    setOpen(false);
    setHover({ rows: 1, cols: 1 });
  };

  return (
    <div ref={wrapRef} className="relative">
      <ToolbarButton
        onClick={() => setOpen((v) => !v)}
        active={open}
        title="Insert table"
      >
        <TableIcon className="w-4 h-4" />
      </ToolbarButton>

      {open ? (
        <div
          className="absolute z-30 top-full left-0 mt-1 bg-white rounded-lg shadow-lg ring-1 ring-gray-200 p-2"
          onMouseLeave={() => setHover({ rows: 1, cols: 1 })}
        >
          <div
            className="grid gap-0.5"
            style={{
              gridTemplateColumns: `repeat(${PICKER_MAX_COLS}, 1.1rem)`,
            }}
          >
            {Array.from({ length: PICKER_MAX_ROWS }).map((_, r) =>
              Array.from({ length: PICKER_MAX_COLS }).map((_, c) => {
                const rows = r + 1;
                const cols = c + 1;
                const active = rows <= hover.rows && cols <= hover.cols;
                return (
                  <button
                    key={`${r}-${c}`}
                    type="button"
                    onMouseEnter={() => setHover({ rows, cols })}
                    onClick={() => commit(rows, cols)}
                    className={`w-[1.1rem] h-[1.1rem] rounded-sm border transition-colors ${
                      active
                        ? "bg-leechiuOrange/30 border-leechiuOrange"
                        : "bg-gray-50 border-gray-200 hover:bg-gray-100"
                    }`}
                    aria-label={`${rows} rows × ${cols} columns`}
                  />
                );
              })
            )}
          </div>
          <div className="mt-2 text-center text-xs text-gray-600 font-medium tabular-nums">
            {hover.rows} × {hover.cols}
          </div>
        </div>
      ) : null}
    </div>
  );
}
